import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WebsiteAnalysisPanel = ({ lead, onAnalyze }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const websiteMetrics = {
    seoScore: lead?.website?.seoScore || 0,
    mobileScore: lead?.website?.mobileScore || 0,
    performanceScore: lead?.website?.performanceScore || 0,
    trafficRank: lead?.website?.trafficRank || 'N/A',
    monthlyVisitors: lead?.website?.monthlyVisitors || 0,
    bounceRate: lead?.website?.bounceRate || 0,
    avgSessionDuration: lead?.website?.avgSessionDuration || 0
  };

  const googleMyBusiness = {
    rating: lead?.gmb?.rating || 0,
    reviewCount: lead?.gmb?.reviewCount || 0,
    photos: lead?.gmb?.photos || 0,
    posts: lead?.gmb?.posts || 0,
    responseRate: lead?.gmb?.responseRate || 0,
    responseTime: lead?.gmb?.responseTime || 'N/A',
    verified: lead?.gmb?.verified || false
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000)?.toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000)?.toFixed(1)}K`;
    return num?.toString();
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-purple-100 p-2 rounded-lg">
            <Icon name="Globe" size={24} className="text-purple-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground">Website & GMB Analysis</h3>
            <p className="text-sm text-muted-foreground">Digital presence and online reputation</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          onClick={() => onAnalyze?.(lead)}
        >
          Refresh Analysis
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center space-x-1 mb-6 border-b border-border">
        <Button
          variant={activeTab === 'overview' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('overview')}
          className={`rounded-b-none ${activeTab === 'overview' ? 'border-b-2 border-primary' : ''}`}
        >
          Overview
        </Button>
        <Button
          variant={activeTab === 'website' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('website')}
          className={`rounded-b-none ${activeTab === 'website' ? 'border-b-2 border-primary' : ''}`}
        >
          Website
        </Button>
        <Button
          variant={activeTab === 'gmb' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('gmb')}
          className={`rounded-b-none ${activeTab === 'gmb' ? 'border-b-2 border-primary' : ''}`}
        >
          Google My Business
        </Button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Globe" size={20} className="text-blue-600" />
                <span className={`text-xs px-2 py-1 rounded-full ${getScoreColor(websiteMetrics?.seoScore)}`}>
                  {getScoreLabel(websiteMetrics?.seoScore)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-1">Website SEO</p>
              <p className="text-2xl font-bold text-foreground">{websiteMetrics?.seoScore}/100</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Smartphone" size={20} className="text-purple-600" />
                <span className={`text-xs px-2 py-1 rounded-full ${getScoreColor(websiteMetrics?.mobileScore)}`}>
                  {getScoreLabel(websiteMetrics?.mobileScore)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-1">Mobile Friendly</p>
              <p className="text-2xl font-bold text-foreground">{websiteMetrics?.mobileScore}/100</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Star" size={20} className="text-green-600" />
                {googleMyBusiness?.verified && (
                  <span className="text-xs px-2 py-1 rounded-full bg-green-500 text-white">
                    Verified
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-1">GMB Rating</p>
              <p className="text-2xl font-bold text-foreground">
                {googleMyBusiness?.rating?.toFixed(1)}/5.0
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{formatNumber(websiteMetrics?.monthlyVisitors)}</p>
              <p className="text-xs text-muted-foreground mt-1">Monthly Visitors</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{websiteMetrics?.bounceRate}%</p>
              <p className="text-xs text-muted-foreground mt-1">Bounce Rate</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{googleMyBusiness?.reviewCount}</p>
              <p className="text-xs text-muted-foreground mt-1">GMB Reviews</p>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{googleMyBusiness?.responseRate}%</p>
              <p className="text-xs text-muted-foreground mt-1">Response Rate</p>
            </div>
          </div>
        </div>
      )}

      {/* Website Tab */}
      {activeTab === 'website' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-semibold text-foreground flex items-center">
                <Icon name="BarChart3" size={16} className="mr-2 text-primary" />
                Performance Metrics
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">SEO Score</span>
                  <span className={`text-sm font-medium px-2 py-1 rounded ${getScoreColor(websiteMetrics?.seoScore)}`}>
                    {websiteMetrics?.seoScore}/100
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Mobile Score</span>
                  <span className={`text-sm font-medium px-2 py-1 rounded ${getScoreColor(websiteMetrics?.mobileScore)}`}>
                    {websiteMetrics?.mobileScore}/100
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Performance</span>
                  <span className={`text-sm font-medium px-2 py-1 rounded ${getScoreColor(websiteMetrics?.performanceScore)}`}>
                    {websiteMetrics?.performanceScore}/100
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-foreground flex items-center">
                <Icon name="TrendingUp" size={16} className="mr-2 text-success" />
                Traffic Analytics
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Traffic Rank</span>
                  <span className="text-sm font-medium text-foreground">
                    {websiteMetrics?.trafficRank}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Monthly Visitors</span>
                  <span className="text-sm font-medium text-foreground">
                    {formatNumber(websiteMetrics?.monthlyVisitors)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Avg. Session</span>
                  <span className="text-sm font-medium text-foreground">
                    {Math.floor(websiteMetrics?.avgSessionDuration / 60)}m {websiteMetrics?.avgSessionDuration % 60}s
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h4 className="font-semibold text-foreground mb-3 flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-2 text-warning" />
              Recommendations
            </h4>
            <div className="space-y-2">
              {lead?.website?.recommendations?.map((rec, index) => (
                <div key={index} className="flex items-start space-x-2 p-2 bg-warning/10 border border-warning/20 rounded">
                  <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                  <p className="text-sm text-foreground">{rec}</p>
                </div>
              )) || (
                <p className="text-sm text-muted-foreground italic">
                  No specific recommendations at this time.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Google My Business Tab */}
      {activeTab === 'gmb' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-semibold text-foreground flex items-center">
                <Icon name="Star" size={16} className="mr-2 text-warning" />
                Reputation Metrics
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Rating</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-foreground">
                      {googleMyBusiness?.rating?.toFixed(1)}/5.0
                    </span>
                    <div className="flex">
                      {[...Array(5)]?.map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={12}
                          className={i < Math.floor(googleMyBusiness?.rating) ? 'text-warning fill-warning' : 'text-gray-300'}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Total Reviews</span>
                  <span className="text-sm font-medium text-foreground">
                    {googleMyBusiness?.reviewCount}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    googleMyBusiness?.verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {googleMyBusiness?.verified ? 'Verified' : 'Unverified'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-foreground flex items-center">
                <Icon name="MessageCircle" size={16} className="mr-2 text-primary" />
                Engagement Metrics
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Response Rate</span>
                  <span className="text-sm font-medium text-foreground">
                    {googleMyBusiness?.responseRate}%
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Response Time</span>
                  <span className="text-sm font-medium text-foreground">
                    {googleMyBusiness?.responseTime}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground">Photos</span>
                  <span className="text-sm font-medium text-foreground">
                    {googleMyBusiness?.photos}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h4 className="font-semibold text-foreground mb-3 flex items-center">
              <Icon name="TrendingUp" size={16} className="mr-2 text-success" />
              GMB Insights
            </h4>
            <div className="space-y-2">
              {lead?.gmb?.insights?.map((insight, index) => (
                <div key={index} className="flex items-start space-x-2 p-2 bg-success/10 border border-success/20 rounded">
                  <Icon name="CheckCircle2" size={16} className="text-success mt-0.5" />
                  <p className="text-sm text-foreground">{insight}</p>
                </div>
              )) || (
                <p className="text-sm text-muted-foreground italic">
                  No GMB insights available yet.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center space-x-3 mt-6 pt-4 border-t border-border">
        <Button variant="primary" size="sm" iconName="ExternalLink" className="flex-1">
          Visit Website
        </Button>
        <Button variant="outline" size="sm" iconName="MapPin" className="flex-1">
          View GMB Profile
        </Button>
      </div>
    </div>
  );
};

export default WebsiteAnalysisPanel;